import { Link } from "react-router-dom";
import { Beaker, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center"><Beaker className="w-5 h-5 text-white" /></div>
            <span className="font-heading font-bold text-lg text-slate-900">ElastoAI</span>
          </Link>
          <Link to="/"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" /> Home</Button></Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12 prose prose-slate">
        <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-slate-500 mb-8">Last updated: 28 May 2026</p>

        <h2>1. Who we are</h2>
        <p>ElastoAI ("we", "us", "our") is an AI-powered platform for polymer and elastomer formulation, analysis, and research. By using ElastoAI you agree to this Privacy Policy.</p>

        <h2>2. What data we collect</h2>
        <ul>
          <li><b>Account data:</b> name, email address, hashed password (bcrypt)</li>
          <li><b>Usage data:</b> chat conversations, formulation requests/responses, uploaded documents and their extracted text, knowledge entries, training-data Q&A pairs, feedback ratings</li>
          <li><b>Technical:</b> timestamps, session IDs, language preference</li>
        </ul>
        <p>We do <b>not</b> collect: payment info (handled by Stripe if/when enabled), IP-based location, browser fingerprints, or third-party analytics.</p>

        <h2>3. How we use your data</h2>
        <ul>
          <li>To provide the chat, formulation, document analysis, and training-data features</li>
          <li>To send your queries to <b>Anthropic (Claude Sonnet 4.5)</b> and <b>Google (Gemini 2.5 Flash for multimodal)</b> via the Emergent Universal LLM service. These providers process your data under their own privacy terms but do not retain inputs for training.</li>
          <li>To improve product quality (aggregate, anonymized usage patterns only)</li>
          <li>The platform owner may review usage data for debugging, support, and abuse prevention</li>
        </ul>

        <h2>4. Data sharing</h2>
        <p>Your data is <b>private to your account</b>. Other users cannot see your chats, documents, formulations, or uploaded files. The only exception is the <b>Shared Knowledge Base</b> — entries the platform owner explicitly marks as "shared" become available to all users' RAG retrieval. Your private knowledge is never auto-shared without action by the owner.</p>
        <p>We do not sell your data. We do not share it with third parties except the LLM providers listed in section 3.</p>

        <h2>5. Data retention</h2>
        <p>We retain your account data and usage data until you delete it (each item has a delete option) or you request account deletion. Deleting your account removes all associated chats, formulations, documents, and training data within 7 days.</p>

        <h2>6. Your rights</h2>
        <ul>
          <li>Access — view all your data via the app</li>
          <li>Delete — delete individual chats, formulations, documents, training entries from within the app</li>
          <li>Request full account deletion — email the platform owner</li>
          <li>EU/UK users: you have GDPR rights including data portability and the right to be forgotten</li>
        </ul>

        <h2>7. Security</h2>
        <p>Passwords are hashed with bcrypt. Authentication uses JWT tokens stored in your browser. Data is stored in a private MongoDB database. All traffic uses HTTPS.</p>

        <h2>8. AI accuracy disclaimer</h2>
        <p>ElastoAI is a decision-support tool, not a replacement for laboratory validation. Generated formulations, predicted properties, and material recommendations are AI-assisted estimates based on available training data and your uploaded knowledge. <b>Always validate any final formulation in a controlled laboratory environment before commercial use.</b> We assume no liability for outcomes derived from use of the platform.</p>

        <h2>9. Children</h2>
        <p>ElastoAI is intended for professional / industrial use and is not directed at children under 16. We do not knowingly collect data from minors.</p>

        <h2>10. Changes</h2>
        <p>We may update this policy. Material changes will be posted on this page with a new "Last updated" date.</p>

        <h2>11. Contact</h2>
        <p>For privacy questions, data deletion requests, or any concern, contact the platform owner at the email used to register your account.</p>

        <div className="mt-12 pt-6 border-t border-slate-200 flex gap-4 text-sm">
          <Link to="/terms" className="text-orange-500 hover:underline">Terms of Service</Link>
          <Link to="/" className="text-orange-500 hover:underline">Home</Link>
        </div>
      </div>
    </div>
  );
}
